import axios from 'axios';

interface TestResult {
    success: boolean;
    message: string;
    details: string[];
    errors?: string[];
}

export const testMasterDataFunctionality = async (): Promise<TestResult> => {
    const details: string[] = [];
    const errors: string[] = [];

    try {
        console.log('🧪 Testing Master Data Functionality...');
        details.push('Starting comprehensive master data test');

        // Test 1: Authentication
        const token = localStorage.getItem('accessToken');
        if (!token) {
            errors.push('No authentication token found. Please login first.');
            return { success: false, message: 'Authentication required', details, errors };
        }
        details.push('✅ Authentication token found');

        // Test 2: Test all GET endpoints
        const entities = ['company', 'customer', 'hub', 'vendor', 'country', 'currency'];

        for (const entity of entities) {
            try {
                const response = await axios.get(`/api/master-data/${entity}`);
                details.push(`✅ GET /api/master-data/${entity} - Status: ${response.status}, Records: ${response.data.data?.length || 0}`);
            } catch (error: any) {
                errors.push(`❌ GET /api/master-data/${entity} failed: ${error.response?.data?.error?.message || error.message}`);
            }
        }

        // Test 3: Test dropdown endpoints
        for (const entity of entities) {
            try {
                const response = await axios.get(`/api/master-data/${entity}/dropdown`);
                details.push(`✅ GET /api/master-data/${entity}/dropdown - Status: ${response.status}, Options: ${response.data.data?.length || 0}`);
            } catch (error: any) {
                details.push(`⚠️ GET /api/master-data/${entity}/dropdown - ${error.response?.status || 'Failed'}`);
            }
        }

        // Test 4: Test stats endpoints
        for (const entity of entities) {
            try {
                const response = await axios.get(`/api/master-data/${entity}/stats`);
                details.push(`✅ GET /api/master-data/${entity}/stats - Status: ${response.status}`);
            } catch (error: any) {
                details.push(`⚠️ GET /api/master-data/${entity}/stats - ${error.response?.status || 'Failed'}`);
            }
        }

        // Test 5: Test CRUD operations with a test currency
        try {
            // Create
            const testCurrency = {
                name: 'Test Currency',
                code: 'TEST',
                symbol: 'T$',
                decimalPlaces: 2,
                isBaseCurrency: false,
                exchangeRate: 75,
                status: 'ACTIVE'
            };

            const createResponse = await axios.post('/api/master-data/currency', testCurrency);
            const createdId = createResponse.data.data._id;
            details.push(`✅ CREATE currency - Status: ${createResponse.status}, ID: ${createdId}`);

            // Read
            const readResponse = await axios.get(`/api/master-data/currency/${createdId}`);
            details.push(`✅ READ currency - Status: ${readResponse.status}, Name: ${readResponse.data.data.name}`);

            // Update
            const updateData = { name: 'Updated Test Currency', exchangeRate: 80 };
            const updateResponse = await axios.put(`/api/master-data/currency/${createdId}`, updateData);
            details.push(`✅ UPDATE currency - Status: ${updateResponse.status}, New Name: ${updateResponse.data.data.name}`);

            // Delete
            const deleteResponse = await axios.delete(`/api/master-data/currency/${createdId}`);
            details.push(`✅ DELETE currency - Status: ${deleteResponse.status}`);

        } catch (error: any) {
            errors.push(`❌ CRUD operations test failed: ${error.response?.data?.error?.message || error.message}`);
        }

        // Test 6: Test search functionality
        try {
            const searchResponse = await axios.get('/api/master-data/company?search=demo&page=1&limit=10');
            details.push(`✅ SEARCH companies - Status: ${searchResponse.status}, Results: ${searchResponse.data.data?.length || 0}`);
        } catch (error: any) {
            errors.push(`❌ Search test failed: ${error.response?.data?.error?.message || error.message}`);
        }

        // Test 7: Test pagination
        try {
            const paginationResponse = await axios.get('/api/master-data/company?page=1&limit=5');
            details.push(`✅ PAGINATION companies - Status: ${paginationResponse.status}, Page size: ${paginationResponse.data.data?.length || 0}`);
        } catch (error: any) {
            errors.push(`❌ Pagination test failed: ${error.response?.data?.error?.message || error.message}`);
        }

        // Test 8: Test filtering
        try {
            const filterResponse = await axios.get('/api/master-data/company?status=ACTIVE');
            details.push(`✅ FILTER companies by status - Status: ${filterResponse.status}, Results: ${filterResponse.data.data?.length || 0}`);
        } catch (error: any) {
            errors.push(`❌ Filter test failed: ${error.response?.data?.error?.message || error.message}`);
        }

        // Summary
        const totalTests = details.filter(d => d.includes('✅')).length;
        const failedTests = errors.length;
        const warningTests = details.filter(d => d.includes('⚠️')).length;

        details.push(`\n📊 Test Summary:`);
        details.push(`✅ Passed: ${totalTests}`);
        details.push(`❌ Failed: ${failedTests}`);
        details.push(`⚠️ Warnings: ${warningTests}`);

        if (errors.length === 0) {
            details.push('\n🎉 All master data functionality is working correctly!');
            return {
                success: true,
                message: 'Master data functionality test completed successfully',
                details
            };
        } else {
            details.push('\n⚠️ Some tests failed. Check the errors for details.');
            return {
                success: false,
                message: 'Master data functionality test completed with errors',
                details,
                errors
            };
        }

    } catch (error: any) {
        errors.push(`❌ Test execution failed: ${error.message}`);
        return {
            success: false,
            message: 'Master data functionality test failed to execute',
            details,
            errors
        };
    }
};

// Test individual entity CRUD operations
export const testEntityCRUD = async (entity: string): Promise<TestResult> => {
    const details: string[] = [];
    const errors: string[] = [];

    try {
        details.push(`🧪 Testing CRUD operations for ${entity}...`);

        // Test data for different entities
        const testData: Record<string, any> = {
            currency: {
                name: `Test ${entity}`,
                code: 'TST',
                symbol: 'T$',
                decimalPlaces: 2,
                isBaseCurrency: false,
                exchangeRate: 1,
                status: 'ACTIVE'
            },
            country: {
                name: `Test ${entity}`,
                code: 'TS',
                iso2Code: 'TS',
                iso3Code: 'TST',
                dialCode: '+999',
                currency: 'USD',
                timezone: 'UTC',
                region: 'Test',
                status: 'ACTIVE'
            }
        };

        const createData = testData[entity];
        if (!createData) {
            errors.push(`No test data available for entity: ${entity}`);
            return { success: false, message: 'No test data available', details, errors };
        }

        // CREATE
        const createResponse = await axios.post(`/api/master-data/${entity}`, createData);
        const createdId = createResponse.data.data._id;
        details.push(`✅ CREATE ${entity} - Status: ${createResponse.status}`);

        // READ
        const readResponse = await axios.get(`/api/master-data/${entity}/${createdId}`);
        details.push(`✅ READ ${entity} - Status: ${readResponse.status}`);

        // UPDATE
        const updateData = { name: `Updated Test ${entity}` };
        const updateResponse = await axios.put(`/api/master-data/${entity}/${createdId}`, updateData);
        details.push(`✅ UPDATE ${entity} - Status: ${updateResponse.status}`);

        // DELETE
        const deleteResponse = await axios.delete(`/api/master-data/${entity}/${createdId}`);
        details.push(`✅ DELETE ${entity} - Status: ${deleteResponse.status}`);

        return {
            success: true,
            message: `CRUD operations for ${entity} completed successfully`,
            details
        };

    } catch (error: any) {
        errors.push(`❌ CRUD test for ${entity} failed: ${error.response?.data?.error?.message || error.message}`);
        return {
            success: false,
            message: `CRUD operations for ${entity} failed`,
            details,
            errors
        };
    }
};

// Make functions available in browser console for testing
if (typeof window !== 'undefined') {
    (window as any).testMasterDataFunctionality = testMasterDataFunctionality;
    (window as any).testEntityCRUD = testEntityCRUD;

    console.log('🧪 Master Data Test Utilities Loaded!');
    console.log('Available functions:');
    console.log('- testMasterDataFunctionality() - Run comprehensive test');
    console.log('- testEntityCRUD(entityName) - Test CRUD for specific entity');
    console.log('- seedMasterData() - Seed sample data');
    console.log('- clearMasterData() - Clear all master data');
} 